// src/components/transport/ChoixTransport.jsx
// ============================================================================
// Le choix du bus d'un élève : sans bus, au mois, ou au ticket (lots 3a et 3a bis)
// ============================================================================
//
// Le bloc de l'inscription, de la réinscription et de la fenêtre du dossier.
// Valeur : { regime: 'aucun' | 'mensuel' | 'ponctuel', sens, mois_depart_id,
//            zone_id, lieu, repere }.
//
// Le prix (décisions de Daniel du 03/11/2026) : quand l'école a des zones de
// trajet, il suit la ZONE choisie, et un abonnement au mois exige la zone et
// le lieu de prise en charge ; sans zone, il suit le tarif de la classe, et
// une classe sans tarif grise la case.
//
// Les montants affichés sont calculés comme le serveur (apiTransport.js) ; le
// serveur recalcule de toute façon à l'enregistrement.
// ============================================================================

import React from 'react';
import CaseACocher from '../ui/CaseACocher';
import ListeDeroulante from '../ui/ListeDeroulante';
import { SENS, fcfa, montantMensuel, prixTicket } from './apiTransport';

const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-50';

export const SANS_BUS = { regime: 'aucun', sens: 'aller_retour', mois_depart_id: '', zone_id: '', lieu: '', repere: '' };

const nomMois = (m) => m?.nom || m?.mois || m?.libelle || `Mois ${m?.id}`;

/** Les zones actives transmises par le serveur (réglages ou état de l'élève), ou []. */
const zonesDe = (zones, reglages) => (Array.isArray(zones) ? zones : (Array.isArray(reglages?.zones) ? reglages.zones : []));

/**
 * Ce qui manque au choix pour que le serveur l'accepte, en une phrase, ou
 * null. À appeler avant d'envoyer une inscription : un refus du serveur
 * annulerait l'inscription entière.
 */
export function transportIncomplet(valeur, { zones, reglages } = {}) {
  if (!valeur || valeur.regime !== 'mensuel' || !zonesDe(zones, reglages).length) return null;
  if (!valeur.zone_id) return 'Bus scolaire : choisissez la zone du trajet, elle fixe le prix.';
  if (!String(valeur.lieu || '').trim()) return 'Bus scolaire : indiquez le lieu de prise en charge (quartier, repère).';
  return null;
}

const ChoixTransport = ({
  tarif, reglages, zones, mois = [], valeur = SANS_BUS, onChange, disabled = false, classe = null, encadre = true
}) => {
  const lesZones = zonesDe(zones, reglages);
  const parZones = lesZones.length > 0;
  const zone = parZones ? lesZones.find((z) => String(z.id) === String(valeur.zone_id || '')) || null : null;
  const tarifClasse = Math.round(Number(tarif) || 0);
  const t = parZones ? Math.round(Number(zone?.tarif) || 0) : tarifClasse;
  const busOuvert = parZones || tarifClasse > 0;
  const pourcentage = reglages?.pourcentage_un_sens || 50;
  const prend = valeur.regime === 'mensuel' || valeur.regime === 'ponctuel';
  const changer = (modifs) => onChange({ ...valeur, ...modifs });

  const description = parZones
    ? 'Le prix suit la zone du trajet de l\'élève.'
    : (tarifClasse > 0 ? `Tarif de la classe : ${fcfa(tarifClasse)} par mois, aller et retour.` : null);
  const texteTicket = t > 0
    ? `Un ticket à chaque trajet : journée ${fcfa(prixTicket(t, 'journee', reglages))}, demi-journée ${fcfa(prixTicket(t, 'demi_journee', reglages))}.`
    : 'Un ticket à chaque trajet, au prix de la zone du jour.';

  const contenu = (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <CaseACocher
          checked={prend}
          onChange={(coche) => changer(coche ? { regime: valeur.regime === 'ponctuel' ? 'ponctuel' : 'mensuel' } : { regime: 'aucun' })}
          disabled={disabled || !busOuvert}
          label="Prend le bus scolaire"
          description={description}
          aide={!busOuvert ? `${classe ? `La classe ${classe}` : 'Sa classe'} n'a pas de prix de bus. Deux façons de l'ouvrir : un tarif de bus pour la classe (Classes et matières, fiche de la classe), ou des zones de trajet pour toute l'école (Outils administratifs, Paramétrage, Scolarité et réductions, section « Zones de trajet du bus »).` : null}
        />
      </div>

      {prend && (
        <>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Façon de payer le bus">
            {[
              { cle: 'mensuel', titre: 'Tous les jours, au mois', texte: 'Ses mensualités de bus sont créées : elles se paient comme la scolarité.' },
              { cle: 'ponctuel', titre: 'De temps en temps, au ticket', texte: texteTicket }
            ].map((option) => (
              <button
                key={option.cle}
                type="button"
                role="radio"
                aria-checked={valeur.regime === option.cle}
                disabled={disabled}
                onClick={() => changer({ regime: option.cle })}
                className={`rounded-lg border p-3 text-left transition ${valeur.regime === option.cle
                  ? 'border-[#246990] bg-[#eef7fb] ring-2 ring-[#246990]/15'
                  : 'border-gray-200 bg-white hover:border-[#246990]/50'}`}
              >
                <span className="block text-sm font-semibold text-gray-900">{option.titre}</span>
                <span className="mt-0.5 block text-xs text-gray-500">{option.texte}</span>
              </button>
            ))}
          </div>

          {parZones && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="transport-zone" className="mb-1 block text-sm text-gray-700">
                  Zone du trajet{valeur.regime === 'mensuel' ? '' : ' (habituelle)'}
                </label>
                <ListeDeroulante
                  id="transport-zone"
                  valeur={valeur.zone_id ? String(valeur.zone_id) : ''}
                  desactive={disabled}
                  onChange={(v) => changer({ zone_id: v })}
                  placeholder="Choisir la zone"
                  options={lesZones.map((z) => ({ valeur: String(z.id), libelle: `${z.nom} - ${fcfa(z.tarif)} par mois` }))}
                />
              </div>
              <div>
                <label htmlFor="transport-lieu" className="mb-1 block text-sm text-gray-700">
                  Lieu de prise en charge{valeur.regime === 'mensuel' ? '' : ' (facultatif)'}
                </label>
                <input
                  id="transport-lieu"
                  type="text"
                  autoComplete="off"
                  maxLength={160}
                  className={CHAMP}
                  placeholder="Quartier"
                  value={valeur.lieu || ''}
                  disabled={disabled}
                  onChange={(e) => changer({ lieu: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="transport-repere" className="mb-1 block text-sm text-gray-700">Repère (facultatif)</label>
                <input
                  id="transport-repere"
                  type="text"
                  autoComplete="off"
                  maxLength={255}
                  className={CHAMP}
                  placeholder="Près de la pharmacie, du marché..."
                  value={valeur.repere || ''}
                  disabled={disabled}
                  onChange={(e) => changer({ repere: e.target.value })}
                />
              </div>
              {zone?.description && <p className="text-xs text-gray-500 sm:col-span-3">{zone.nom} : {zone.description}</p>}
            </div>
          )}

          {valeur.regime === 'mensuel' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="transport-sens" className="mb-1 block text-sm text-gray-700">Trajets</label>
                <ListeDeroulante
                  id="transport-sens"
                  valeur={valeur.sens || 'aller_retour'}
                  desactive={disabled}
                  onChange={(v) => changer({ sens: v })}
                  options={Object.entries(SENS).map(([cle, libelle]) => ({
                    valeur: cle,
                    libelle: `${libelle}${t > 0 ? ` - ${fcfa(montantMensuel(t, cle, pourcentage))} par mois` : ''}`
                  }))}
                />
              </div>
              <div>
                <label htmlFor="transport-depart" className="mb-1 block text-sm text-gray-700">Premier mois payé</label>
                <ListeDeroulante
                  id="transport-depart"
                  valeur={valeur.mois_depart_id ? String(valeur.mois_depart_id) : ''}
                  desactive={disabled}
                  onChange={(v) => changer({ mois_depart_id: v })}
                  options={[{ valeur: '', libelle: 'Mois en cours' }, ...mois.map((m) => ({ valeur: String(m.id), libelle: nomMois(m) }))]}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (!encadre) return contenu;
  return (
    <section className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">Transport scolaire</h3>
      {contenu}
    </section>
  );
};

export default ChoixTransport;
